import { useEffect, useState } from "react";
import { format, isBefore, startOfDay, endOfDay, startOfMonth, subDays } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DateRange } from "react-day-picker";
import { Button } from "@akuna/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

interface StockPeriodHeroProps {
  dateFrom: Date;
  dateTo: Date;
  quantityIn: number;
  quantityOut: number;
  adjustments: number;
  lowStockCount: number;
  criticalStockCount: number;
  onDateFromChange: (d: Date) => void;
  onDateToChange: (d: Date) => void;
}

export function StockPeriodHero({
  dateFrom,
  dateTo,
  quantityIn,
  quantityOut,
  adjustments,
  lowStockCount,
  criticalStockCount,
  onDateFromChange,
  onDateToChange,
}: StockPeriodHeroProps) {
  const { t, i18n } = useTranslation("reports");
  const locale = i18n.language === "pt" ? ptBR : enUS;
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>({ from: dateFrom, to: dateTo });
  const [selectionStep, setSelectionStep] = useState<"from" | "to">("from");

  useEffect(() => {
    setRange({ from: dateFrom, to: dateTo });
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (calendarOpen) setSelectionStep("from");
  }, [calendarOpen]);

  const periodLabel = `${format(dateFrom, "dd MMM yyyy", { locale })} – ${format(dateTo, "dd MMM yyyy", { locale })}`;

  function applyQuick(type: "today" | "7d" | "30d" | "month") {
    const now = new Date();
    let from: Date;
    const to: Date = endOfDay(now);
    if (type === "today") {
      from = startOfDay(now);
    } else if (type === "7d") {
      from = startOfDay(subDays(now, 6));
    } else if (type === "30d") {
      from = startOfDay(subDays(now, 29));
    } else {
      from = startOfMonth(now);
    }
    onDateFromChange(from);
    onDateToChange(to);
    setRange({ from, to });
    setSelectionStep("from");
  }

  function handleDayClick(day: Date) {
    const clicked = startOfDay(day);
    if (selectionStep === "from") {
      setRange({ from: clicked, to: undefined });
      onDateFromChange(clicked);
      onDateToChange(endOfDay(clicked));
      setSelectionStep("to");
      return;
    }
    const from = range?.from ? startOfDay(range.from) : clicked;
    const nextFrom = isBefore(clicked, from) ? clicked : from;
    const nextTo = isBefore(clicked, from) ? from : clicked;
    setRange({ from: nextFrom, to: nextTo });
    onDateFromChange(nextFrom);
    onDateToChange(endOfDay(nextTo));
    setSelectionStep("from");
    setCalendarOpen(false);
  }

  const isQuickActive = (type: "today" | "7d" | "30d" | "month") => {
    const now = new Date();
    if (type === "today") {
      return (
        format(dateFrom, "yyyy-MM-dd") === format(startOfDay(now), "yyyy-MM-dd") &&
        format(dateTo, "yyyy-MM-dd") === format(endOfDay(now), "yyyy-MM-dd")
      );
    }
    if (type === "7d") return format(dateFrom, "yyyy-MM-dd") === format(startOfDay(subDays(now, 6)), "yyyy-MM-dd");
    if (type === "30d") return format(dateFrom, "yyyy-MM-dd") === format(startOfDay(subDays(now, 29)), "yyyy-MM-dd");
    if (type === "month") return format(dateFrom, "yyyy-MM-dd") === format(startOfMonth(now), "yyyy-MM-dd");
    return false;
  };

  return (
    <div className="rounded-2xl border bg-card p-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      {/* Left — context */}
      <div className="flex flex-col gap-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
            {t("stockOperation")}
          </span>
        </div>
        <h2 className="text-2xl font-bold tracking-tight truncate">{periodLabel}</h2>
        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground flex-wrap">
          <span className="font-semibold text-foreground">{quantityIn}</span>
          <span>{t("stockEntries").toLowerCase()}</span>
          <span>·</span>
          <span className="font-semibold text-foreground">{quantityOut}</span>
          <span>{t("stockExits").toLowerCase()}</span>
          <span>·</span>
          <span className="font-semibold text-foreground">{adjustments}</span>
          <span>{t("stockAdjustments").toLowerCase()}</span>
          {(lowStockCount > 0 || criticalStockCount > 0) && (
            <>
              <span>·</span>
              <span className="font-semibold text-amber-600">{lowStockCount}</span>
              <span>{t("lowStock").toLowerCase()}</span>
              <span>·</span>
              <span className="font-semibold text-red-600">{criticalStockCount}</span>
              <span>{t("criticalStock").toLowerCase()}</span>
            </>
          )}
        </div>
      </div>

      {/* Right — controls */}
      <div className="flex flex-col gap-3 shrink-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {(["today", "7d", "30d", "month"] as const).map((q) => {
            const labels = {
              today: t("quickToday"),
              "7d": t("quick7d"),
              "30d": t("quick30d"),
              month: t("quickThisMonth"),
            };
            return (
              <Button
                key={q}
                size="sm"
                variant={isQuickActive(q) ? "default" : "outline"}
                className="h-7 px-3 text-xs rounded-full"
                onClick={() => applyQuick(q)}
              >
                {labels[q]}
              </Button>
            );
          })}
        </div>

        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-2 justify-start">
              <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate max-w-[220px]">{periodLabel}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={range}
              onDayClick={handleDayClick}
              numberOfMonths={2}
              locale={locale}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
